import React, { useState, useEffect } from "react";
import {
	Table,
	Row,
	Col,
	Card,
	notification,
	Select,
	Spin,
	Tag,
	Badge,
	Tooltip,
	DatePicker,
} from "antd";

import {
	AppstoreAddOutlined,
	EyeOutlined,
	ScheduleOutlined,
} from "@ant-design/icons";

import { Link, useLocation, useNavigate } from "react-router-dom";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import dashboardReportService from "../../services/dashboardReport";
import FormListComponent from "../../components/serviceDeskFormList";
import { useMediaQuery } from "react-responsive";
import userService from "../../services/user";
const { Option } = Select;
const { RangePicker } = DatePicker;

const DashboardStatusReport = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const initialStatus = location.state?.statusFilter || "Processing";
	const initialForm = location.state?.selectedForm || null;

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [totalDisplay, setTotalDisplay] = useState("");
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);

	const [selectedForm, setSelectedForm] = useState(initialForm);
	const [statusFilter, setStatusFilter] = useState(initialStatus);
	const [users, setUsers] = useState(null);

	// NEW FILTERS
	const [selectedUser, setSelectedUser] = useState([]);
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);

	const taskStatus = ["Approved", "Disapproved", "Return", "Processing"];

	const getUsers = async () => {
		let response;
		response = await userService.getUsers();
		setUsers(response.data);
	};


	// Fetch data
	const getData = async () => {
		if (!statusFilter) return;

		setLoading(true);

		try {
			const response =
				await dashboardReportService.getDashboardStatusReport(
					currentPage,
					statusFilter,
					selectedForm,
					selectedUser.length > 0 ? selectedUser : null,
					fromDate,
					toDate
				);

			if (response.success) {
				const items = response.items || [];
				const meta = response.meta || {};

				setData(items);

				const perPage = Number(meta.per_page) || items.length || 10;
				setItemsPerPage(perPage);

				const current = Number(meta.current_page) || currentPage || 1;
				if (meta.current_page) setCurrentPage(current);

				const total =
					meta.total !== undefined ? Number(meta.total) : items.length;

				setTotalDataCount(total);
				setTotalDisplay(meta.total_display || String(total));
			} else {
				setData([]);
				setTotalDataCount(0);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description:
					error?.response?.data?.message || "Something went wrong",
			});
			setData([]);
			setTotalDataCount(0);
		} finally {
			setLoading(false);
		}
	};

	// Filters change
	useEffect(() => {
		setData([]);
		setCurrentPage(1);
		getData();
		
	}, [statusFilter, selectedForm, selectedUser, fromDate, toDate]);

	// Page change
	useEffect(() => {
		getData();
		getUsers();
	}, [currentPage]);

	const columns = [
		{
			title: "Form",
			dataIndex: "form_name",
			width: "15%",
		},
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			width: "15%",
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			width: "40%",
		},
		{
			title: "Status",
			dataIndex: "status",
			width: "10%",
			render: (status) => (
				<Tag
					color={
						(status === "Processing" && "blue") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red") ||
						(status === "Pending" && "orange")
					}
				>
					{status}
				</Tag>
			),
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
		},
		{
			title: "Logs",
			key: "reason",
			width: "10%",
			render: (_, record) => (
				<Tooltip title="Activity Log">
					<span
						onClick={() =>
							navigate("/activity-log", {
								state: {
									form_id: record.form_id,
									key: record.key,
								},
							})
						}
						style={{ cursor: "pointer" }}
					>
						<ScheduleOutlined style={{ color: "#1677ff" }} />
					</span>
				</Tooltip>
			),
		},
		{
			title: "Action",
			width: "10%",
			render: (_, record) => (
				<Link to={`/${record.form_slug}/details/${record.key}`}>
					<EyeOutlined />
				</Link>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<AppstoreAddOutlined />}
						title="Status Overview"
					/>
				</Col>
			</Row>

			<Card>
				<Row gutter={24} style={{ marginBottom: 16 }}>
	<Col span={4}>
		<Select
			showSearch
			size="large"
			placeholder="Status"
			value={statusFilter}
			onChange={(value) => {
				setStatusFilter(value);
				setCurrentPage(1);
			}}
			allowClear
			style={{ width: "100%" }}
		>
			{taskStatus.map((status) => (
				<Option key={status} value={status}>
					{status}
				</Option>
			))}
		</Select>
	</Col>

	<Col span={4}>
		<FormListComponent
			onChange={(value) => {
				setSelectedForm(value);
				setCurrentPage(1);
			}}
			selectedFormId={selectedForm}
		/>
	</Col>

	<Col span={4}>
		<Select
			mode="multiple"
			showSearch
			size="large"
			placeholder="Select Initiator"
			value={selectedUser}
			onChange={(value) => {
				setSelectedUser(value);
				setCurrentPage(1);
			}}
			allowClear
			style={{ width: "100%" }}
			filterOption={(input, option) =>
				String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
			}
		>
			{users?.map((user) => (
				<Option key={user.id} value={user.id}>
					{user.name}
				</Option>
			))}
		</Select>
	</Col>

	<Col span={4}>
		<RangePicker
			size="large"
			style={{ width: "100%" }}
			onChange={(dates, dateStrings) => {
				setFromDate(dateStrings[0]);
				setToDate(dateStrings[1]);
				setCurrentPage(1);
			}}
		/>
	</Col>

	<Col span={4}>
		<Badge count={totalDisplay} overflowCount={999} style={{ width: "100%" }}>
			<Card title="Total Records" style={{ border: "none" }} />
		</Badge>
	</Col>
</Row>
			</Card>

			<Card>
				<Spin spinning={loading}>
					<Table
						rowKey="record_id"
						columns={columns}
						dataSource={data}
						pagination={{
							current: currentPage,
							pageSize: itemsPerPage,
							total: totalDataCount,
							onChange: (page) => setCurrentPage(page),
						}}
						scroll={{ y: 400 }}
					/>
				</Spin>
			</Card>
		</DefaultLayout>
	);
};

export default DashboardStatusReport;